// Smart Alerts API client — user-defined rules that fire on new regulations.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(body || `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface AlertFilterCriteria {
  jurisdictions: string[];
  categories: string[];
  keywords: string[];
  status: string[];
  regulation_types: string[];
  ai_relevance_min?: number | null;
}

export interface Alert {
  id: string;
  name: string;
  description?: string | null;
  semantic_query?: string | null;
  filter_criteria: AlertFilterCriteria;
  trigger_on_new: boolean;
  trigger_on_status_change: boolean;
  trigger_on_effective_date: boolean;
  delivery_channels: string[];
  frequency: string;
  semantic_threshold: number;
  is_active: boolean;
  is_snoozed_until?: string | null;
  last_triggered_at?: string | null;
  trigger_count: number;
  created_at: string;
  updated_at: string;
}

export interface AlertCreate {
  name: string;
  description?: string | null;
  semantic_query?: string | null;
  filter_criteria?: Partial<AlertFilterCriteria>;
  trigger_on_new?: boolean;
  trigger_on_status_change?: boolean;
  trigger_on_effective_date?: boolean;
  delivery_channels?: string[];
  frequency?: string;
  semantic_threshold?: number;
}

export type AlertUpdate = Partial<AlertCreate & { is_active: boolean }>;

export interface AlertTrigger {
  id: string;
  alert_id: string;
  regulation_id: string;
  match_score?: number | null;
  match_reason?: string | null;
  trigger_type: string;
  regulation_source?: string | null;
  regulation_title?: string | null;
  regulation_url?: string | null;
  jurisdiction_name?: string | null;
  delivered_at?: string | null;
  dismissed_at?: string | null;
  read_at?: string | null;
  latency_seconds?: number | null;
  explanation?: string | null;
  created_at: string;
}

/** SRS #166 */
export interface AlertPreview {
  lookback_days: number; evaluated: number; matched: number; per_week: number;
  verdict: 'too_narrow' | 'quiet' | 'good' | 'busy' | 'too_broad'; advice: string;
  samples: Array<{ regulation_id: string; title: string | null; jurisdiction: string | null; match_score: number; match_reason: string; date: string }>;
}
export interface AlertQuality {
  matches_90d: number; per_week: number; dismissed: number; dismiss_rate: number | null;
  latency_p50_seconds: number | null; latency_p95_seconds: number | null; within_15_min_pct: number | null; verdict: string; advice: string;
}

export interface AlertTestResult {
  alert_id: string;
  evaluated: number;
  matched: number;
  matches: Array<{
    regulation_id: string;
    title: string | null;
    jurisdiction: string | null;
    match_score: number;
    match_reason: string;
  }>;
}

export const listAlerts = (): Promise<Alert[]> =>
  fetch(`${API_BASE_URL}/api/v1/alerts`, { headers: authHeaders() }).then(r => handle<Alert[]>(r));

export const createAlert = (payload: AlertCreate): Promise<Alert> =>
  fetch(`${API_BASE_URL}/api/v1/alerts`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
  }).then(r => handle<Alert>(r));

export const updateAlert = (id: string, payload: AlertUpdate): Promise<Alert> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}`, {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
  }).then(r => handle<Alert>(r));

export const deleteAlert = (id: string): Promise<void> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}`, { method: 'DELETE', headers: authHeaders() }).then(r => handle<void>(r));

export const listAlertTriggers = (id: string): Promise<AlertTrigger[]> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}/triggers`, { headers: authHeaders() }).then(r => handle<AlertTrigger[]>(r));

export const testAlert = (id: string): Promise<AlertTestResult> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}/test`, { method: 'POST', headers: authHeaders() }).then(r => handle<AlertTestResult>(r));

export const previewAlert = (payload: AlertCreate, lookbackDays = 30): Promise<AlertPreview> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/preview?lookback_days=${lookbackDays}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }).then(r => handle<AlertPreview>(r));
export const alertQuality = (id: string): Promise<AlertQuality> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}/quality`, { headers: authHeaders() }).then(r => handle<AlertQuality>(r));
export const snoozeAlert = (id: string, days: number): Promise<Alert> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}/snooze?days=${days}`, { method: 'POST', headers: authHeaders() }).then(r => handle<Alert>(r));
export const unsnoozeAlert = (id: string): Promise<Alert> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${id}/unsnooze`, { method: 'POST', headers: authHeaders() }).then(r => handle<Alert>(r));
export const dismissTrigger = (alertId: string, triggerId: string): Promise<{ dismissed: boolean }> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${alertId}/triggers/${triggerId}/dismiss`, { method: 'POST', headers: authHeaders() }).then(r => handle<{ dismissed: boolean }>(r));
export const dismissAllTriggers = (alertId: string): Promise<{ dismissed: number }> =>
  fetch(`${API_BASE_URL}/api/v1/alerts/${alertId}/triggers/dismiss-all`, { method: 'POST', headers: authHeaders() }).then(r => handle<{ dismissed: number }>(r));
